import { makeAutoObservable, observable, ObservableSet } from 'mobx'
import { MarkType, Node, NodeType } from 'prosemirror-model'
import { EditorState } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'

export class ProseState {
  view?: EditorView
  activeMarks: ObservableSet<MarkType> = observable.set()
  activeNodes: ObservableSet<NodeType> = observable.set()

  /**Selection is entirely contained within these types */
  containingNodeTypes: ObservableSet<NodeType> = observable.set()

  constructor(view?: EditorView) {
    makeAutoObservable(this)
    if (view) {
      this.view = view
      this.updateFromState(view.state)
    }
  }

  setView = (view: EditorView) => {
    this.view = view
    this.updateFromState(view.state)
  }

  updateFromState = (state: EditorState) => {
    this.setActiveMarks(state)
    this.setActiveNodes(state)
    this.setContainingNodesTypes(state)
  }

  setActiveMarks(state: EditorState) {
    this.activeMarks.clear()

    let { $from, empty, ranges } = state.selection

    if (empty) {
      if (!state.storedMarks) {
        // As per https://discuss.prosemirror.net/t/understanding-togglemark-and-storedmarks/2682
        // storedMarks == null means "don't do anything special for the next insertion" (which mean inherit the marks
        // around the cursor)
        // storedMarks === [] means "make sure the next insertion doesn't have any marks set".
        // storedMarks === [mark1, mark2] means "only apply mark1 and mark2 to the next insertion".
        $from.marks().forEach((m) => this.activeMarks.add(m.type))
      } else {
        state.storedMarks.forEach((m) => this.activeMarks.add(m.type))
      }
    } else {
      ranges.forEach((r) => {
        state.doc.nodesBetween(r.$from.pos, r.$to.pos, (n) => {
          n.marks.forEach((m) => {
            this.activeMarks.add(m.type)
          })
        })
      })
    }
  }

  setActiveNodes(state: EditorState) {
    const foundNodeTypes = new Set<NodeType>()
    for (const { $from, $to } of state.selection.ranges) {
      state.doc.nodesBetween($from.pos, $to.pos, (n) => {
        this.activeNodes.add(n.type)
        foundNodeTypes.add(n.type)
      })
    }

    for (const nodeType of this.activeNodes) if (!foundNodeTypes.has(nodeType)) this.activeNodes.delete(nodeType)
  }

  /**Find types for nodes that completely contain all selection ranges */
  setContainingNodesTypes(state: EditorState) {
    const rangeNodesList: Array<Set<Node>> = []

    for (const { $from, $to } of state.selection.ranges) {
      const foundNodes = new Set<Node>()

      const rangeNodes = new Set<Node>()
      rangeNodesList.push(rangeNodes)

      let depth = $from.depth
      let parent: Node | undefined
      do {
        parent = $from.node(depth)
        foundNodes.add(parent)
        depth--
      } while (depth > 0 && parent)

      depth = $to.depth
      parent = undefined
      do {
        parent = $to.node(depth)
        if (foundNodes.has(parent)) rangeNodes.add(parent)
        depth--
      } while (depth > 0 && parent)
    }

    const containingNodeTypes = new Set<NodeType>()
    for (const rangeNode of rangeNodesList[0])
      if (rangeNodesList.every((l) => l.has(rangeNode))) {
        containingNodeTypes.add(rangeNode.type)
        this.containingNodeTypes.add(rangeNode.type)
      }

    for (const nodeType of this.containingNodeTypes)
      if (!containingNodeTypes.has(nodeType)) this.containingNodeTypes.delete(nodeType)
  }
}
